import { useEffect, useState, type ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { completeLogin, login, loginErrorKey, restartLogin } from './oidc'
import '@/styles/errors.css'

/**
 * Экраны входа и выхода, вёрстка по Login.dc.html (`.auth-wrap` / `.auth-card`).
 *
 * Пароль здесь не вводится и вводиться не должен: вход идёт через Keycloak (OIDC, код
 * авторизации), и форма пароля на нашей стороне означала бы, что пароль видит приложение,
 * а не сервер входа. Поэтому от макета взята рамка и состояния — «войти», «входим»,
 * «не удалось войти», «выходим», «выход не подтверждён», — а поля почты и пароля, «Забыли
 * пароль?», «Запомнить меня» и блокировка после попыток живут на странице Keycloak.
 */
export function AuthCard({ busy, children }: { busy?: boolean; children: ReactNode }) {
  const { t, locale, setLocale } = useApp()
  return (
    <div className="auth-wrap">
      <div className="auth-stack">
        <main className="auth-card" aria-labelledby="auth-title" aria-busy={busy || undefined}>
          <div className="auth-head">
            <span className="logo-ph">[Логотип]</span>
            <span className="t-lg strong">{t('app.title')}</span>
          </div>
          {children}
        </main>
        <footer className="auth-foot t-xs muted">
          <div className="seg" role="group" aria-label={t('auth.language')}>
            <button type="button" aria-pressed={locale === 'ru'} onClick={() => setLocale('ru')}>RU</button>
            <button type="button" aria-pressed={locale === 'en'} onClick={() => setLocale('en')}>EN</button>
          </div>
        </footer>
      </div>
    </div>
  )
}

function Title({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="auth-title">
      <h1 id="auth-title" className="h1">{title}</h1>
      {sub && <p className="t-sm secondary">{sub}</p>}
    </div>
  )
}

/** Экран для неаутентифицированного пользователя: одна кнопка, ведущая на сервер входа. */
export function SignIn() {
  const { t } = useApp()
  const [pending, setPending] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  return (
    <AuthCard busy={pending}>
      <Title title={t('auth.signIn.title')} sub={t('auth.required')} />
      {failed && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span className="fg">{t(failed)}</span>
        </div>
      )}
      <button
        type="button"
        className="btn btn-primary btn-wide"
        aria-busy={pending || undefined}
        disabled={pending}
        onClick={() => {
          setPending(true)
          setFailed(null)
          // Уход на Keycloak — переход страницы; сюда управление возвращается только при
          // отказе (сервер входа недоступен), и тогда кнопка обязана ожить с причиной.
          void login().catch((cause: unknown) => {
            setFailed(loginErrorKey(cause))
            setPending(false)
          })
        }}
      >
        {pending && <span className="spinner" aria-hidden="true" />}
        {pending ? t('auth.signingIn') : t('auth.signIn')}
      </button>
      <span className="help">{t('auth.signIn.help')}</span>
    </AuthCard>
  )
}

/** Возврат от Keycloak: обменивает код на токен и уводит туда, откуда пришли. */
export function AuthCallback() {
  const { t } = useApp()
  const [error, setError] = useState<string | null>(null)
  const [retrying, setRetrying] = useState(false)

  useEffect(() => {
    completeLogin()
      .then((target) => window.location.replace(target))
      .catch((cause: unknown) => setError(loginErrorKey(cause)))
  }, [])

  if (error) {
    return (
      <AuthCard busy={retrying}>
        <Title title={t('auth.failed')} />
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span className="fg">{t(error)}</span>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-wide"
          disabled={retrying}
          onClick={() => {
            setRetrying(true)
            void restartLogin().catch((cause: unknown) => {
              setError(loginErrorKey(cause))
              setRetrying(false)
            })
          }}
        >
          {retrying && <span className="spinner" aria-hidden="true" />}
          {t('auth.retry')}
        </button>
      </AuthCard>
    )
  }
  return <Pending title={t('auth.signingIn')} />
}

/**
 * Выход: «выходим» и «сервер входа не подтвердил выход».
 *
 * Второе — не мелочь: локальный токен уже стёрт, а сессия в Keycloak может быть жива, и
 * следующий вход пройдёт без пароля. Сказать это вслух и дать повторить — честнее, чем
 * показать экран входа, будто выход удался.
 */
export function SigningOut({ failed, onRetry }: { failed: boolean; onRetry: () => void }) {
  const { t } = useApp()
  if (!failed) return <Pending title={t('auth.signingOut')} />
  return (
    <AuthCard>
      <Title title={t('auth.logoutFailed.title')} />
      <div className="alert alert-warning" role="alert">
        <Icon name="alert" />
        <span className="fg">{t('auth.logoutFailed')}</span>
      </div>
      <button type="button" className="btn btn-primary btn-wide" onClick={onRetry}>
        {t('auth.retry')}
      </button>
    </AuthCard>
  )
}

function Pending({ title }: { title: string }) {
  return (
    <AuthCard busy>
      <div className="row" role="status" style={{ gap: 10 }}>
        <span className="spinner auth-spinner" aria-hidden="true" />
        <h1 id="auth-title" className="h2" style={{ margin: 0 }}>{title}</h1>
      </div>
    </AuthCard>
  )
}
